import { WorkflowActionType } from '@/gen/cortado/v1/cortado_pb'

// Action config JSON shapes, mirrored from the temporal activity configs:
//   UPDATE_FIELD          = { field, value }
//   SEND_TEMPLATED_EMAIL  = { to_field, to_email, cc?, bcc?, subject_template, body_template_html }
//   CREATE_TASK           = { title, description, due_in_days, assigned_to_user_id }
//   WAIT                  = { duration }
//   SEND_WEBHOOK          = { endpoint_id }
//
// The serialized JSON keys are the literal snake_case the backend expects; the
// discriminant `type` is a TS-only field and is never written to the JSON.

export type UpdateFieldConfig = {
  type: WorkflowActionType.UPDATE_FIELD
  field: string
  value: string
}

export type SendTemplatedEmailConfig = {
  type: WorkflowActionType.SEND_TEMPLATED_EMAIL
  toField: string
  toEmail: string
  cc: string[]
  bcc: string[]
  subjectTemplate: string
  bodyTemplateHtml: string
}

export type CreateTaskConfig = {
  type: WorkflowActionType.CREATE_TASK
  title: string
  description: string
  dueInDays: number
  assignedToUserId: string
}

export type SendWebhookConfig = {
  type: WorkflowActionType.SEND_WEBHOOK
  endpointId: string
}

export type WaitUnit = 'minutes' | 'hours' | 'days' | 'weeks'

export type WaitConfig = {
  type: WorkflowActionType.WAIT
  amount: number
  unit: WaitUnit
}

export type ActionConfig =
  | UpdateFieldConfig
  | SendTemplatedEmailConfig
  | CreateTaskConfig
  | SendWebhookConfig
  | WaitConfig

export function defaultActionConfig(actionType: WorkflowActionType): ActionConfig {
  switch (actionType) {
    case WorkflowActionType.UPDATE_FIELD:
      return { type: WorkflowActionType.UPDATE_FIELD, field: '', value: '' }
    case WorkflowActionType.SEND_TEMPLATED_EMAIL:
      return {
        type: WorkflowActionType.SEND_TEMPLATED_EMAIL,
        toField: '',
        toEmail: '',
        cc: [],
        bcc: [],
        subjectTemplate: '',
        bodyTemplateHtml: '',
      }
    case WorkflowActionType.CREATE_TASK:
      return {
        type: WorkflowActionType.CREATE_TASK,
        title: '',
        description: '',
        dueInDays: 0,
        assignedToUserId: '',
      }
    case WorkflowActionType.SEND_WEBHOOK:
      return { type: WorkflowActionType.SEND_WEBHOOK, endpointId: '' }
    case WorkflowActionType.WAIT:
    case WorkflowActionType.UNSPECIFIED:
    default:
      return { type: WorkflowActionType.WAIT, amount: 1, unit: 'days' }
  }
}

export function serializeActionConfig(config: ActionConfig): string {
  switch (config.type) {
    case WorkflowActionType.UPDATE_FIELD:
      return JSON.stringify({ field: config.field, value: config.value })
    case WorkflowActionType.SEND_TEMPLATED_EMAIL:
      return JSON.stringify(
        {
          to_field: config.toField,
          to_email: config.toEmail,
          cc: splitList(config.cc),
          bcc: splitList(config.bcc),
          subject_template: config.subjectTemplate,
          body_template_html: config.bodyTemplateHtml,
        }
      )
    case WorkflowActionType.CREATE_TASK:
      return JSON.stringify(
        {
          title: config.title,
          description: config.description,
          due_in_days: toInt(config.dueInDays),
          assigned_to_user_id: config.assignedToUserId,
        }
      )
    case WorkflowActionType.SEND_WEBHOOK:
      return JSON.stringify({ endpoint_id: config.endpointId })
    case WorkflowActionType.WAIT:
      return JSON.stringify({ duration: serializeWaitDuration(config) })
  }
}

export function deserializeActionConfig(
  actionType: WorkflowActionType,
  json: string
): ActionConfig {
  let raw: Record<string, unknown>
  try {
    const parsed = JSON.parse(json)
    raw = parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : {}
  } catch {
    return defaultActionConfig(actionType)
  }

  switch (actionType) {
    case WorkflowActionType.UPDATE_FIELD:
      return {
        type: WorkflowActionType.UPDATE_FIELD,
        field: str(raw.field),
        value: str(raw.value),
      }
    case WorkflowActionType.SEND_TEMPLATED_EMAIL:
      return {
        type: WorkflowActionType.SEND_TEMPLATED_EMAIL,
        toField: str(raw.to_field),
        toEmail: str(raw.to_email),
        cc: splitList(raw.cc),
        bcc: splitList(raw.bcc),
        subjectTemplate: str(raw.subject_template),
        bodyTemplateHtml: str(raw.body_template_html),
      }
    case WorkflowActionType.CREATE_TASK:
      return {
        type: WorkflowActionType.CREATE_TASK,
        title: str(raw.title),
        description: str(raw.description),
        dueInDays: toInt(raw.due_in_days),
        assignedToUserId: str(raw.assigned_to_user_id),
      }
    case WorkflowActionType.SEND_WEBHOOK:
      return { type: WorkflowActionType.SEND_WEBHOOK, endpointId: str(raw.endpoint_id) }
    case WorkflowActionType.WAIT:
    case WorkflowActionType.UNSPECIFIED:
    default:
      return deserializeWaitConfig(raw.duration ?? raw.delay)
  }
}

export function legacyDelayFromActionConfig(json: string): string | undefined {
  let raw: Record<string, unknown>
  try {
    const parsed = JSON.parse(json)
    raw = parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : {}
  } catch {
    return undefined
  }

  const delay = str(raw.delay).trim()
  return delay === '' ? undefined : delay
}

export function serializeWaitDuration(config: WaitConfig): string {
  const amount = Math.max(1, toInt(config.amount))
  switch (config.unit) {
    case 'minutes':
      return `${amount}m`
    case 'hours':
      return `${amount}h`
    case 'weeks':
      return `${amount * 7}d`
    case 'days':
    default:
      return `${amount}d`
  }
}

export function formatWaitDuration(config: WaitConfig): string {
  const amount = Math.max(1, toInt(config.amount))
  const singular = config.unit.slice(0, -1)
  return `${amount} ${amount === 1 ? singular : config.unit}`
}

function deserializeWaitConfig(duration: unknown): WaitConfig {
  const parsed = parseWaitDuration(str(duration))
  return {
    type: WorkflowActionType.WAIT,
    amount: parsed.amount,
    unit: parsed.unit,
  }
}

// serializeWaitFromParts builds a canonical duration string ("Nm"/"Nh"/"Nd")
// from an amount + unit pair. Weeks are expanded to days. Used by the graph
// wait-node editor, which stores the duration string directly (the server owns
// the JSON), not the legacy amount/unit ActionConfig shape.
export function serializeWaitFromParts(amount: number, unit: WaitUnit): string {
  return serializeWaitDuration({ type: WorkflowActionType.WAIT, amount, unit })
}

// parseWaitDuration is exported so graph wait editors can derive amount/unit
// inputs from the stored canonical duration string.
export function parseWaitDuration(value: string): { amount: number; unit: WaitUnit } {
  const duration = value.trim().toLowerCase()
  if (duration === '') {
    return { amount: 1, unit: 'days' }
  }

  const days = duration.match(/^(\d+)d$/)
  if (days) {
    const amount = toInt(days[1])
    return amount >= 7 && amount % 7 === 0
      ? { amount: amount / 7, unit: 'weeks' }
      : { amount: Math.max(1, amount), unit: 'days' }
  }

  const hours = duration.match(/^(\d+)h$/)
  if (hours) {
    return { amount: Math.max(1, toInt(hours[1])), unit: 'hours' }
  }

  const minutes = duration.match(/^(\d+)m$/)
  if (minutes) {
    return { amount: Math.max(1, toInt(minutes[1])), unit: 'minutes' }
  }

  const hourMinute = duration.match(/^(?:(\d+)h)?(?:(\d+)m)?$/)
  if (hourMinute && (hourMinute[1] || hourMinute[2])) {
    const totalMinutes = toInt(hourMinute[1]) * 60 + toInt(hourMinute[2])
    return { amount: Math.max(1, totalMinutes), unit: 'minutes' }
  }

  return { amount: 1, unit: 'days' }
}

// splitList accepts either an array or a comma-separated string and returns a
// trimmed, empty-filtered array.
function splitList(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.map((item) => String(item).trim()).filter((item) => item !== '')
  }
  if (typeof value === 'string') {
    return value
      .split(',')
      .map((item) => item.trim())
      .filter((item) => item !== '')
  }
  return []
}

function toInt(value: unknown): number {
  const parsed = Math.trunc(Number(value))
  return Number.isFinite(parsed) ? parsed : 0
}

function str(value: unknown): string {
  return typeof value === 'string' ? value : ''
}
